import { useLocation } from "react-router";
import { useCallback, useEffect, useState } from "react";
import { DEFAULT_PRICE_RANGE, DEFAULT_SORTING } from "./constants";
import { useAppContext } from "context/app-context";
import { isValidNumberWithinBoundary } from "utils/numbers";
import { getBrandDiscounts } from "./requests";

export const useGetBrandDiscounts = () => {
  const { search, pathname } = useLocation();
  const searchToObject = () => {
    const searchParams = new URLSearchParams(search);
    return Object.fromEntries(searchParams.entries());
  };
  const searchParams = searchToObject();
  const isValidNumber = (value) =>
    isValidNumberWithinBoundary(value, {
      min: Number(DEFAULT_PRICE_RANGE.priceMin),
      max: Number(DEFAULT_PRICE_RANGE.priceMax)
    });

  const getInitialPriceRange = () => {
    const { priceMin: paramPriceMin, priceMax: paramPriceMax } = searchParams;

    if (paramPriceMin || paramPriceMax) {
      const [min, max] = [paramPriceMin, paramPriceMax].map((range) =>
        isValidNumber(range) ? range : ""
      );

      return { priceMin: min, priceMax: max, search: true };
    }

    return DEFAULT_PRICE_RANGE;
  };

  const {
    sessionInfo: { featureToggles }
  } = useAppContext();
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [priceRange, setPriceRange] = useState(getInitialPriceRange());

  const [loading, setLoading] = useState(true);
  const [brandDiscounts, setBrandDiscounts] = useState(null);

  const currentPath = pathname.replace(/\/merchant|\//g, "");
  const pageZeroIndexed = -1;
  const groupedListing = !!featureToggles.isGroupListingsEnabled;

  const fetchBrandDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedBrandDiscounts = await getBrandDiscounts({
        currentPath,
        page: pageZeroIndexed,
        perPage: pageZeroIndexed,
        sorting,
        priceRange: {
          priceMin: priceRange.priceMin || DEFAULT_PRICE_RANGE.priceMin,
          priceMax: priceRange.priceMax || DEFAULT_PRICE_RANGE.priceMax
        },
        groupedListing
      });

      setBrandDiscounts(fetchedBrandDiscounts);
    } catch (error) {
      setBrandDiscounts(null);
    } finally {
      setLoading(false);
    }
  }, [pageZeroIndexed, sorting, priceRange, currentPath, groupedListing]);

  useEffect(() => {
    void fetchBrandDiscounts();
  }, [fetchBrandDiscounts]);

  return {
    loading,
    brandDiscounts,
    fetchBrandDiscounts,
    filters: {
      setSorting,
      setPriceRange,
      sorting,
      priceRange
    }
  };
};
